import { encodeAbiParameters, parseAbiParameters } from 'viem'
import { Call } from '../../../utils/multicall'
import {
  getCompoundV2Tokens,
  getCompoundV2Comptroller,
} from '../../public-data/addresses/compoundV2'
import {
  getPositionLensFor,
  LENS_FAMILY,
  PositionLensAbi,
} from '../position-lens'

/** Words per lens row — must match `PositionLens.compoundV2`'s documented layout. */
export const COMPOUND_V2_LENS_WORDS = 3
/** Leading `uint8 assetsInOk` before the row count. */
export const COMPOUND_V2_LENS_HEADER_BYTES = 1

/**
 * True when this chain's Compound-fork reads go through `PositionLens`. Derived
 * from the address map so the builder and the converter cannot disagree about
 * the layout — both call this rather than each deciding.
 */
export const usesCompoundV2Lens = (chainId: string): boolean =>
  getPositionLensFor(chainId, LENS_FAMILY.COMPOUND_V2) !== undefined

export const buildCompoundV2UserCall = (
  chainId: string,
  lender: string,
  account: string,
): Call[] => {
  const tokens = getCompoundV2Tokens(chainId, lender).sort((a, b) =>
    a.cToken.toLowerCase().localeCompare(b.cToken.toLowerCase()),
  )
  const comptroller = getCompoundV2Comptroller(chainId, lender)

  /**
   * ONE call instead of 3 per cToken plus the comptroller — 132 cTokens across
   * BNB's five forks, 107 across Ethereum's seven, for accounts that are in a
   * handful of markets at most.
   *
   * The lens probes `balanceOf` + `borrowBalanceStored` (two view reads of stored
   * state) and only pays `balanceOfUnderlying` + `borrowBalanceCurrent` — which
   * ACCRUE, and are the expensive half — on markets that answer. It emits a row
   * for a market held, borrowed, or merely entered, which is the same set this
   * converter keeps.
   *
   * A chain without a deployment keeps the per-token walk below; that is the
   * rollout switch and the emergency revert both.
   */
  const lens = getPositionLensFor(chainId, LENS_FAMILY.COMPOUND_V2)
  if (lens && comptroller && tokens.length > 0) {
    const payload = encodeAbiParameters(
      parseAbiParameters('address[], address'),
      [
        tokens.map((t) => t.cToken as `0x${string}`),
        comptroller as `0x${string}`,
      ],
    )
    return [
      {
        address: lens,
        name: 'getUserData',
        params: [[{ family: LENS_FAMILY.COMPOUND_V2, payload }], account],
        abi: PositionLensAbi as any,
      },
    ]
  }

  let calls: Call[] = tokens.flatMap((tk) => [
    {
      address: tk.cToken,
      name: 'balanceOfUnderlying',
      params: [account],
    },
    {
      address: tk.cToken,
      name: 'borrowBalanceCurrent',
      params: [account],
    },
    {
      address: tk.cToken,
      name: 'balanceOf',
      params: [account],
    },
  ])

  calls = [
    ...calls,
    {
      address: comptroller!,
      name: 'getAssetsIn',
      params: [account],
    },
  ]

  return calls
}
